import React, { FormEvent, useEffect, useState } from 'react'
import { observer, useLocalObservable } from 'mobx-react-lite'
import { comparer, reaction } from 'mobx'
import clsx from 'clsx'

import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Select
} from '@material-ui/core'

import { CheckCircle, CheckCircleOutline, RadioButtonUnchecked as CircleUnchecked } from '@material-ui/icons'

import { BootstrapInput } from '../../BootstrapInput'
import { WbPreviewPrintTable } from '../../WbPreviewPrintTable'
import { PrintMsBarcodesCheckbox } from '../../PrintMsBarcodesCheckbox'
import { formatWbSupplyDisplayName } from '../../WbOrdersSupplyDropdown'

import { useMessages } from '../../../hooks/snackbarHooks'
import { useApi, useLogic } from '../../../hooks/storeHook'
import { useCheckboxStyles } from '../../../hooks/checkboxStylesHook'
import { useBoolState, useLazyEffect } from '../../../hooks/commonHooks'
import { useSettingsPageStyles } from '../../../hooks/settingsPageStylesHook'
import { useDefaultMenuItemStyles } from '../../../hooks/defaultSelectStylesHook'

import { MemoryTableData } from '../../../store/memoryTableData'

import { ensureString } from '../../../common/tsUtils'
import { fixMsAppScrollProps } from '../../../common/msAppUtils'
import { isUiException } from '../../../common/requestValidationError'

import { WbAccountIdProps } from '../../../types/accountProps'
import { WbPrintOrderColumnId } from '../../../types/wbColumns'
import { RequestCancelledError } from '../../../types/requestCancelledError'

import { WbSupplyModel } from '../../../server/mpsklad_core/Models/WbSupplyModel'
import { WbPrintOptions } from '../../../server/mpsklad_core/Models/WbPrintOptions'
import { WbPrintOrderModel } from '../../../server/mpsklad_core/Models/WbPrintOrderModel'

export type WildberriesLabelsPrintFormProps = {
  wbSupplies: WbSupplyModel[]

  onPrinted: VoidFunction
} & WbAccountIdProps

type WbLabelOptionsState = {
  wbSupplyId: string
  closeSupply: boolean
  hasPurchaseList: boolean
  hasMsBarcodes: boolean
  hasArticleCounts: boolean
  hasWbBarcode: boolean
  doubleUpLabels: boolean
  hasWbOrderId: boolean

  readonly requestData: Readonly<WbPrintOptions>
}

export const WildberriesLabelsPrintForm =
  observer(
    ({wbAccountId, wbSupplies, onPrinted}: WildberriesLabelsPrintFormProps) => {
      const api = useApi()
      const logic = useLogic()

      const {showSuccess} = useMessages()

      const [isSubmitting, setSubmitting, setSubmitted] = useBoolState()
      const [submitError, setSubmitError] = useState<string | null>(null)

      const classes = useSettingsPageStyles()
      const menuClasses = useDefaultMenuItemStyles()
      const checkboxClasses = useCheckboxStyles()

      const options =
        useLocalObservable<WbLabelOptionsState>(() => ({
          // NOTE: React effects are required for initial values that depend on props or state
          wbSupplyId: wbSupplies[0].id,
          closeSupply: true,
          hasPurchaseList: true,
          hasMsBarcodes: false,
          hasArticleCounts: false,
          hasWbBarcode: false,
          doubleUpLabels: false,
          hasWbOrderId: false,

          get requestData() {
            return {
              accountId: wbAccountId,
              wbSupplyId: this.wbSupplyId,
              closeSupply: this.closeSupply,
              hasPurchaseList: this.hasPurchaseList,
              hasMsBarcodes: this.hasMsBarcodes,
              hasArticleCounts: this.hasArticleCounts,
              hasWbBarcode: this.hasWbBarcode,
              doubleUpLabels: this.doubleUpLabels,
              hasWbOrderId: this.hasWbOrderId
            } as const satisfies Readonly<WbPrintOptions>
          }
        } satisfies WbLabelOptionsState))

      const [wbPrintOrders] = useState(
        () => new MemoryTableData<WbPrintOrderModel, WbPrintOrderColumnId>('wbPrintOrders'))

      useEffect(() => {options.wbSupplyId = wbSupplies[0].id}, [options, wbSupplies])

      useLazyEffect(() =>
        reaction(
          () => options.requestData,
          async () => {
            wbPrintOrders.data = []

            try {
              wbPrintOrders.data = await api.previewWbLabelsPreemptive(options.requestData)
            } catch (e) {
              if (e instanceof RequestCancelledError) {
                // Ignore
                return
              } else {
                throw e
              }
            }
          }, {
            fireImmediately: true,
            equals: comparer.structural
          }))

      const onSubmit =
        async (e: FormEvent) => {
          e.preventDefault()

          if (isSubmitting) {
            return
          }

          setSubmitting()
          setSubmitError(null)

          try {
            const printResult = await api.label.printWbSupply(options.requestData)

            if (printResult.errorCount === 0) {
              showSuccess('Создано!')
            } else {
              showSuccess(`Создано! Отправлений с ошибками: ${printResult.errorCount}.`)
            }

            const {zipName, fileUrls} = logic.getZipNameAndFiles(printResult, 'wb_labels')

            await logic.downloadFilesAsZip(fileUrls, zipName)

            onPrinted()
          } catch (ex) {
            if (isUiException(ex)) {
              setSubmitError(ex.response.data.uiMessage)
            } else {
              throw ex
            }
          } finally {
            setSubmitted()
          }
        }

      return (
        <Box paddingTop={0}>
          <FormHelperText className={classes.helperText}>
            Печать этикеток возможна только для заказов, добавленных в поставку.
            Возможна печать до 300 заказов в одном файле.
          </FormHelperText>

          <form onSubmit={onSubmit}>
            <Grid
              container
              className={clsx(classes.supplySettingsContainer, 'default-border')}
              direction="column"
              xs={12}
              spacing={3}
            >
              <Grid item container xs={12}>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend" className={classes.label}>
                    Поставка
                  </FormLabel>

                  <Select
                    value={options.wbSupplyId}
                    onChange={e => options.wbSupplyId = ensureString(e.target.value)}
                    MenuProps={fixMsAppScrollProps}
                    fullWidth
                    input={<BootstrapInput/>}
                  >
                    {
                      wbSupplies.map(wbSupply =>
                        <MenuItem
                          className={menuClasses.menuItem}
                          key={wbSupply.id}
                          value={wbSupply.id}
                        >
                          {formatWbSupplyDisplayName(wbSupply)}
                        </MenuItem>)
                    }
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Grid container>
                  <Grid item container xs={6}>
                    <FormControl component="fieldset">

                      <Box>
                        <FormLabel component="legend" className={classes.label}>
                          <span className={classes.dash}>—</span>
                          Что печатать
                        </FormLabel>
                      </Box>

                      <FormGroup>
                        <FormControlLabel
                          className={clsx(checkboxClasses.checkbox)}
                          control={
                            <Checkbox
                              color="primary"
                              name="feedCheckbox"
                              checked
                              disabled
                              icon={<CircleUnchecked/>}
                              checkedIcon={<CheckCircleOutline/>}
                            />
                          }
                          label="Лента"
                        />

                        <FormControlLabel
                          className={clsx(checkboxClasses.checkbox,
                            options.hasPurchaseList && checkboxClasses.checkedCheckbox)}
                          control={
                            <Checkbox
                              color="primary"
                              name="purchaseListCheckbox"
                              checked={options.hasPurchaseList}
                              onChange={e => options.hasPurchaseList = e.target.checked}
                              icon={<CircleUnchecked/>}
                              checkedIcon={<CheckCircle/>}
                            />
                          }
                          label="Список покупок"
                        />

                        <PrintMsBarcodesCheckbox
                          checked={options.hasMsBarcodes}
                          onChange={checked => options.hasMsBarcodes = checked}
                        />

                        <FormControlLabel
                          className={clsx(checkboxClasses.checkbox,
                            options.hasWbBarcode && checkboxClasses.checkedCheckbox)}
                          control={
                            <Checkbox
                              color="primary"
                              name="hasWbBarcodeCheckbox"
                              checked={options.hasWbBarcode}
                              onChange={e => options.hasWbBarcode = e.target.checked}
                              icon={<CircleUnchecked/>}
                              checkedIcon={<CheckCircle/>}
                            />
                          }
                          label="Штрихкод товара в Wildberries"
                        />
                      </FormGroup>
                    </FormControl>
                  </Grid>

                  <Grid item container xs={6}>
                    <Grid item xs={12}>
                      <FormControl component="fieldset">

                        <Box>
                          <FormLabel component="legend" className={classes.label}>
                            <span className={classes.dash}>—</span>
                            Опции печати
                          </FormLabel>
                        </Box>

                        <FormGroup>
                          <FormControlLabel
                            className={clsx(checkboxClasses.checkbox,
                              options.doubleUpLabels && checkboxClasses.checkedCheckbox)}
                            control={
                              <Checkbox
                                color="primary"
                                name="doubleUpLabelsCheckbox"
                                checked={options.doubleUpLabels}
                                onChange={e => options.doubleUpLabels = e.target.checked}
                                icon={<CircleUnchecked/>}
                                checkedIcon={<CheckCircle/>}
                              />
                            }
                            label="Задваивать каждую этикетку"
                          />

                          <FormControlLabel
                            className={clsx(checkboxClasses.checkbox,
                              options.hasArticleCounts && checkboxClasses.checkedCheckbox)}
                            control={
                              <Checkbox
                                color="primary"
                                name="hasArticleCountsCheckbox"
                                checked={options.hasArticleCounts}
                                onChange={e => options.hasArticleCounts = e.target.checked}
                                icon={<CircleUnchecked/>}
                                checkedIcon={<CheckCircle/>}
                              />
                            }
                            label="Добавить список артикулов"
                          />

                          <FormControlLabel
                            className={clsx(checkboxClasses.checkbox,
                              options.hasWbOrderId && checkboxClasses.checkedCheckbox)}
                            control={
                              <Checkbox
                                color="primary"
                                name="hasWbOrderIdCheckbox"
                                checked={options.hasWbOrderId}
                                onChange={e => options.hasWbOrderId = e.target.checked}
                                icon={<CircleUnchecked/>}
                                checkedIcon={<CheckCircle/>}
                              />
                            }
                            label="Добавить идентификатор сборочного задания в Маркетплейсе"
                          />

                          <FormControl component="fieldset">
                            <FormGroup>
                              <FormControlLabel
                                className={clsx(checkboxClasses.checkbox,
                                  options.closeSupply && checkboxClasses.checkedCheckbox)}
                                control={
                                  <Checkbox
                                    color="primary"
                                    name="closeSupplyCheckbox"
                                    checked={options.closeSupply}
                                    onChange={e => options.closeSupply = e.target.checked}
                                    icon={<CircleUnchecked/>}
                                    checkedIcon={<CheckCircleOutline/>}
                                  />
                                }
                                label="Закрыть поставку после печати"
                              />

                              <FormHelperText
                                className={clsx(classes.helperText)}
                              >
                                Печать штрихкода поставки возможна только после закрытия поставки.
                                <br/>
                                При успешном создании этикеток поставка будет закрыта (передана в доставку).
                              </FormHelperText>
                            </FormGroup>
                          </FormControl>

                        </FormGroup>
                      </FormControl>
                    </Grid>
                  </Grid>

                </Grid>
              </Grid>

            </Grid>

            <Box>
              <button
                className={clsx(classes.saveButton, 'default-button')}
                disabled={isSubmitting}
                onClick={onSubmit}>
                <p>{isSubmitting ? 'Создаём...' : 'Создать этикетки'}</p>
              </button>
            </Box>

            <Grid
              container
              className={clsx(classes.supplySettingsContainer, 'default-border')}
              direction="column">
              {
                !!submitError &&
                <Grid item>
                  <FormLabel error>
                    {submitError}
                  </FormLabel>
                </Grid>
              }

              <Grid item xl={12}>
                <Box>
                  <FormLabel component="legend" className={clsx(classes.label)}>
                    <span className={classes.dash}>—</span>
                    Предпросмотр
                  </FormLabel>
                </Box>

                <WbPreviewPrintTable orders={wbPrintOrders}/>
              </Grid>
            </Grid>
          </form>
        </Box>
      )
    })